import cn from 'classnames';
import { ChangeEvent, FC, ReactNode, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppSelector } from '@/shared/hooks';
import { Button, Input, Modal } from '@/shared/ui';

import { DocumentTemplateSummary, templatesModel } from '@/entities/templates';

import {
  communityTemplatesMessages,
  RegistryAssetImportResult,
  RegistryAssetSelectorDialog,
} from '@/features/communityTemplates';

import { encounterSchemaMessages } from '../../config/i18n';

import styles from './AddSchemaDocumentDialog.module.scss';

type AddSchemaDocumentDialogProps = {
  isOpen: boolean;
  language: string;
  attachedTemplateSeriesIds: Set<string>;
  organizationId?: string;
  onSelect: (templateSeriesId: string) => void;
  onClose: () => void;
};

type TemplateGroupKey = 'organization' | 'community' | 'system';

type TemplateGroup = {
  key: TemplateGroupKey;
  labelMessageId: keyof typeof encounterSchemaMessages;
  templates: DocumentTemplateSummary[];
};

const GROUP_ORDER: TemplateGroupKey[] = ['organization', 'community', 'system'];

const GROUP_LABEL_KEYS: Record<
  TemplateGroupKey,
  keyof typeof encounterSchemaMessages
> = {
  organization: 'addDocumentGroupOrganization',
  community: 'addDocumentGroupCommunity',
  system: 'addDocumentGroupSystem',
};

const getGroupKey = (template: DocumentTemplateSummary): TemplateGroupKey => {
  if (template.source_kind === 'community') {
    return 'community';
  }

  if (template.source_kind === 'system' || template.is_system) {
    return 'system';
  }

  return 'organization';
};

const renderHighlight = (text: string, query: string): ReactNode => {
  const trimmed = query.trim();

  if (!trimmed) {
    return text;
  }

  const lower = text.toLowerCase();
  const needle = trimmed.toLowerCase();
  const index = lower.indexOf(needle);

  if (index === -1) {
    return text;
  }

  return (
    <>
      {text.slice(0, index)}
      <mark className={styles.match}>
        {text.slice(index, index + trimmed.length)}
      </mark>
      {text.slice(index + trimmed.length)}
    </>
  );
};

export const AddSchemaDocumentDialog: FC<AddSchemaDocumentDialogProps> = (
  props,
) => {
  const {
    isOpen,
    language,
    attachedTemplateSeriesIds,
    organizationId,
    onSelect,
    onClose,
  } = props;

  const { formatMessage } = useIntl();

  const documentTemplates = useAppSelector(
    templatesModel.selectors.selectDocumentTemplates,
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [isRegistryOpen, setIsRegistryOpen] = useState(false);

  const availableTemplates = useMemo(() => {
    const seenSeriesIds = new Set<string>();

    return documentTemplates.filter((template) => {
      if (!template.series_id || seenSeriesIds.has(template.series_id)) {
        return false;
      }

      if (attachedTemplateSeriesIds.has(template.series_id)) {
        return false;
      }

      if (template.language !== language) {
        return false;
      }

      const isOrgScoped = Boolean(template.organization_id);

      if (isOrgScoped && template.organization_id !== organizationId) {
        return false;
      }

      seenSeriesIds.add(template.series_id);
      return true;
    });
  }, [attachedTemplateSeriesIds, documentTemplates, language, organizationId]);

  const filteredTemplates = useMemo(() => {
    const trimmed = searchQuery.trim().toLowerCase();

    if (!trimmed) {
      return availableTemplates;
    }

    return availableTemplates.filter((template) => {
      const haystack = [
        template.name,
        template.tab_title,
        template.description ?? '',
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(trimmed);
    });
  }, [availableTemplates, searchQuery]);

  const groups = useMemo<TemplateGroup[]>(() => {
    const buckets: Record<TemplateGroupKey, DocumentTemplateSummary[]> = {
      organization: [],
      community: [],
      system: [],
    };

    for (const template of filteredTemplates) {
      buckets[getGroupKey(template)].push(template);
    }

    return GROUP_ORDER.filter((key) => buckets[key].length > 0).map((key) => ({
      key,
      labelMessageId: GROUP_LABEL_KEYS[key],
      templates: buckets[key],
    }));
  }, [filteredTemplates]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSelect = (templateSeriesId: string) => {
    onSelect(templateSeriesId);
    setSearchQuery('');
    onClose();
  };

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  const handleOpenRegistry = () => {
    setIsRegistryOpen(true);
  };

  const handleCloseRegistry = () => {
    setIsRegistryOpen(false);
  };

  const handleRegistryImport = (result: RegistryAssetImportResult) => {
    if (result.document_template_series_id) {
      onSelect(result.document_template_series_id);
      setSearchQuery('');
      onClose();
    }
  };

  const renderEmptyState = () => {
    if (availableTemplates.length === 0) {
      return (
        <div className={cn('p3', styles.emptyState)}>
          {formatMessage(encounterSchemaMessages.addDocumentEmpty)}
        </div>
      );
    }

    return (
      <div className={cn('p3', styles.emptyState)}>
        {formatMessage(encounterSchemaMessages.addDocumentNoResults)}
      </div>
    );
  };

  return (
    <Modal
      data-component="add-schema-document-dialog"
      isOpen={isOpen}
      title={formatMessage(encounterSchemaMessages.addDocumentTitle)}
      onCancel={handleClose}
      hideFooter
      bodyClassName={styles.body}
    >
      <div className={styles.container}>
        <div className={styles.searchRow}>
          <Input
            name="addDocumentSearch"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={formatMessage(
              encounterSchemaMessages.addDocumentSearchPlaceholder,
            )}
            autoFocus
            className={styles.searchInput}
          />
          <Button
            variant="gray"
            size="medium"
            className={styles.communityButton}
            onClick={handleOpenRegistry}
          >
            {formatMessage(communityTemplatesMessages.registrySelectorTitle)}
          </Button>
        </div>

        {filteredTemplates.length === 0 ? (
          renderEmptyState()
        ) : (
          <div className={styles.list}>
            {groups.map((group) => (
              <section key={group.key} className={styles.group}>
                <h4 className={cn('p4b', styles.groupTitle)}>
                  {formatMessage(encounterSchemaMessages[group.labelMessageId])}
                </h4>
                <ul className={styles.groupList}>
                  {group.templates.map((template) => (
                    <li key={template.series_id}>
                      <button
                        type="button"
                        className={cn(styles.card)}
                        onClick={() => handleSelect(template.series_id)}
                      >
                        <span className={cn('h7', styles.cardName)}>
                          {renderHighlight(template.name, searchQuery)}
                        </span>
                        {template.tab_title &&
                        template.tab_title !== template.name ? (
                          <span className={cn('p3', styles.cardSubtitle)}>
                            {renderHighlight(template.tab_title, searchQuery)}
                          </span>
                        ) : null}
                        {template.description ? (
                          <span className={cn('p3', styles.cardDescription)}>
                            {renderHighlight(template.description, searchQuery)}
                          </span>
                        ) : null}
                        <span className={styles.cardMeta}>
                          <span className={cn('p4b', styles.cardBadge)}>
                            {template.type
                              .toLowerCase()
                              .split('_')
                              .map(
                                (part) =>
                                  part.charAt(0).toUpperCase() + part.slice(1),
                              )
                              .join(' ')}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      <RegistryAssetSelectorDialog
        isOpen={isRegistryOpen}
        family="document_template"
        organizationId={organizationId}
        language={language}
        onSelect={handleRegistryImport}
        onClose={handleCloseRegistry}
      />
    </Modal>
  );
};
